import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Color from 'color';
import { useTranslation } from '@gamiphy/react-i18n';
import { WheelDto, WheelOptionDto, WheelOptionDtoRewardTypeEnum } from '@coretava/api/events-service';
import { formatWheelOptionReward } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import IndicatorIcon from './assets/IndicatorIcon';

type Props = {
  resultOption?: WheelOptionDto,
  afterRotation: () => void
  wheel: WheelDto,
  size?: number,
  centerSize?: number,
};

const Wheel: React.FC<Props> = ({
  resultOption,
  afterRotation,
  wheel,
  size = 250,
  centerSize = 58,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const selectedOptionIndex = React.useMemo(
    () => wheel?.wheelOptions.findIndex((option) => option.id === resultOption?.id),
    [wheel, resultOption],
  );

  const sliceAngle = React.useMemo(() => 360 / wheel.wheelOptions.length, [wheel]);

  const sliceWdith = React.useMemo(() => (size - 16) * Math.tan(((sliceAngle / 2) * Math.PI) / 180) * 2, [sliceAngle, size]);

  const getSliceColor = React.useCallback((rewardType: WheelOptionDtoRewardTypeEnum, index: number) => {
    switch (true) {
      case rewardType === WheelOptionDtoRewardTypeEnum.FreeShipping:
        return theme.palette.primary.main;
      case rewardType === WheelOptionDtoRewardTypeEnum.Monetary:
      case rewardType === WheelOptionDtoRewardTypeEnum.Percentage:
        if (Color(theme.palette.primary.main).isDark()) {
          return index % 2 !== 0 ? theme.palette.primary.main : Color(theme.palette.primary.main).lighten(0.5).hex();
        }
        return index % 2 !== 0 ? theme.palette.primary.main : Color(theme.palette.primary.main).darken(0.5).hex();
      case rewardType === WheelOptionDtoRewardTypeEnum.FreeSku:
        return '#fff';
      default:
        return theme.palette.primary.main;
    }
  }, [theme]);

  const getDividerColor = React.useCallback((previousRewardType: WheelOptionDtoRewardTypeEnum, rewardType: WheelOptionDtoRewardTypeEnum) => {
    if (previousRewardType === WheelOptionDtoRewardTypeEnum.FreeSku && rewardType === WheelOptionDtoRewardTypeEnum.FreeSku) {
      return theme.palette.primary.main;
    }
    return '#fff';
  }, [theme]);

  return (
    <Box
      width={size}
      height={size}
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: 'relative',
        borderRadius: '50%',
        backgroundColor: 'primary.main',
      }}
    >
      <Box
        width={centerSize}
        height={centerSize}
        sx={{
          backgroundColor: 'primary.main',
          borderRadius: '50%',
          position: 'absolute',
          zIndex: 10,
        }}
      />
      <Box
        width={centerSize - 22}
        height={centerSize - 22}
        sx={{
          backgroundColor: 'primary.main',
          boxShadow: '0px 1.62181px 3.24362px 1.62181px rgba(0, 0, 0, 0.48)',
          borderRadius: '50%',
          position: 'absolute',
          zIndex: 11,
        }}
      />
      <Box position="absolute" top={-30} zIndex={1}>
        <IndicatorIcon color={theme.palette.primary.main} />
      </Box>
      <Box
        onTransitionEnd={afterRotation}
        width={`calc(${size}px - 16px)`}
        height={`calc(${size}px - 16px)`}
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          position: 'relative',
          transform: resultOption ? `rotate(calc(${3600 - ((selectedOptionIndex ?? 0) * (1 / (wheel.wheelOptions.length))) * 360}deg))` : 'unset',
          transition: 'transform 10s',
        }}
      >
        {wheel.wheelOptions.map((option, index, arr) => (
          <Box
            key={option.id}
            sx={{
              width: '1.5px',
              height: '50%',
              position: 'absolute',
              bottom: '50%',
              zIndex: 4,
              backgroundColor: getDividerColor(
                index === 0 ? arr[arr.length - 1]?.rewardType : arr[index - 1]?.rewardType,
                arr[index]?.rewardType,
              ),
              transform: `rotate(calc(${sliceAngle / 2}deg + ${index - 1} * ${sliceAngle}deg))`,
              transformOrigin: 'bottom',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                borderRadius: '50%',
                width: '10px',
                height: '10px',
                backgroundColor: 'primary.main',
                border: '1px solid #fff',
                flexShrink: 0,
                mt: '-5px',
              }}
            />
          </Box>
        ))}
        <Box
          width="100%"
          height="100%"
          sx={{
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {wheel.wheelOptions.map((option, index) => (
            <Box
              key={option.id}
              width={sliceWdith}
              height="100%"
              borderRadius={0}
              sx={{
                backgroundColor: getSliceColor(option.rewardType, index),
                clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)',
                pt: '55%',
                pb: `${centerSize / 1.4}px`,
                px: `${0.4 * sliceWdith}px`,
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -100%) rotate(calc(${sliceAngle}deg * ${index}))`,
                transformOrigin: '50% 100%',
              }}
            >
              {option.rewardType === WheelOptionDtoRewardTypeEnum.FreeSku ? (
                <Box component="img" src={option.image} maxWidth={sliceWdith / 5} maxHeight="100%" />
              ) : (
                <Typography
                  sx={{
                    transform: 'rotate(-90deg)',
                    textAlign: 'center',
                    width: '100%',
                  }}
                  color="#fff"
                  fontWeight={700}
                >
                  {formatWheelOptionReward(option, t)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Wheel;
